import {
  FiltroPeriodo,
  FiltroPlanta,
  GraficoM3PorDia,
  HistorialImportaciones,
  KpiCard,
  type RangoFechas,
  UploadCsvBlend,
  calcularRangoPreset,
} from '@/components/dominio';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useKpis } from '@/hooks/useKpis';
import { esSoloLab, esSoloOperadorFiori, puedeOperarColadas } from '@/lib/cliente';
import { RANGO_MAXIMO_DIAS } from '@adelante/shared';
import { differenceInDays } from 'date-fns';
import { AlertTriangle, Beaker, Package, Pencil, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';

/**
 * Pantalla principal `/`. Orquesta:
 *   - Filtros (periodo + planta)
 *   - 4 KPI cards
 *   - Gráfico de m³ por día
 *   - Upload de CSV
 *   - Historial de importaciones (con fila "procesando" optimista)
 */
export function DashboardPantalla() {
  // Usuarios "solo Laboratorio" aterrizan directo en su sección — el
  // dashboard de KPIs de planta no les sirve.
  if (esSoloLab()) {
    return <Navigate to="/lab/muestras" replace />;
  }
  // Usuarios "solo OperadorFiori" (choferes de las autocargables): el
  // dashboard de KPIs no les sirve — aterrizan directo en el formulario
  // de registro, que es lo que van a usar la mayoría de las veces. Ver
  // sus registros pasados queda a un toque de distancia (nav "Mis Registros").
  if (esSoloOperadorFiori()) {
    return <Navigate to="/coladas/registrar-fiori" replace />;
  }

  const [periodo, setPeriodo] = useState<RangoFechas>(() => calcularRangoPreset('mes_actual'));
  const [idPlanta, setIdPlanta] = useState<number | null>(null);
  const [archivoSubiendo, setArchivoSubiendo] = useState<string | null>(null);

  const diasRango = differenceInDays(
    new Date(`${periodo.hasta}T00:00:00Z`),
    new Date(`${periodo.desde}T00:00:00Z`),
  );
  const rangoValido = diasRango >= 0 && diasRango <= RANGO_MAXIMO_DIAS;

  const kpisQuery = useKpis(
    rangoValido ? { desde: periodo.desde, hasta: periodo.hasta, idPlanta } : null,
  );
  const kpis = kpisQuery.data?.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Control de Concreto</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Dashboard de producción de las plantas Blend del Grupo Adelante.
        </p>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Filtros</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-4">
          <FiltroPeriodo valor={periodo} onChange={setPeriodo} />
          <FiltroPlanta idPlanta={idPlanta} onChange={setIdPlanta} />
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          titulo="Batches"
          valor={kpis?.total_batches ?? 0}
          formato="numero"
          icono={<Package className="h-4 w-4" aria-hidden="true" />}
          cargando={kpisQuery.isLoading}
          destacado
        />
        <KpiCard
          titulo="m³ producidos"
          valor={kpis?.total_m3 ?? 0}
          formato="m3"
          icono={<TrendingUp className="h-4 w-4" aria-hidden="true" />}
          cargando={kpisQuery.isLoading}
        />
        <KpiCard
          titulo="% con alarma"
          valor={kpis?.pct_con_alarma ?? 0}
          formato="porcentaje"
          icono={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
          cargando={kpisQuery.isLoading}
        />
        <KpiCard
          titulo="% receta modificada"
          valor={kpis?.pct_receta_modificada ?? 0}
          formato="porcentaje"
          icono={<Pencil className="h-4 w-4" aria-hidden="true" />}
          cargando={kpisQuery.isLoading}
        />
      </div>

      {/* Gráfico */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Beaker className="h-4 w-4" aria-hidden="true" />
            m³ por día
          </CardTitle>
        </CardHeader>
        <CardContent>
          <GraficoM3PorDia
            datos={kpis?.m3_por_dia ?? []}
            cargando={kpisQuery.isLoading || !rangoValido}
          />
          {kpisQuery.error && (
            <p className="mt-2 text-sm text-destructive">
              Error al cargar KPIs: {kpisQuery.error.message}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Upload — operativo (Admin/Operador). Ingeniería solo lee. */}
      {puedeOperarColadas() && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Subir CSV Blend</CardTitle>
          </CardHeader>
          <CardContent>
            <UploadCsvBlend
              onSubiendoCambio={(sub) => setArchivoSubiendo(sub ? 'archivo en proceso…' : null)}
            />
          </CardContent>
        </Card>
      )}

      {/* Historial */}
      <HistorialImportaciones subiendoArchivo={archivoSubiendo} />
    </div>
  );
}
